import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAdminModeEnabled } from '../../selectors/user';
import { ensureINSToken, handleFailedRefresh } from '../../services/interactiveServiceFetch';
import toaster from '../../utils/toaster';
import { disconnectConnection, fetchConnections, startConnection } from '../service';
import {
    CONNECTION_EVENTS,
    CONNECTION_ID_PARAM,
    CONNECTION_URL_PARAMS,
    ERROR_PARAM,
    GENERIC_ERROR_KEY,
    RESULT_PARAM,
    TOAST_AUTO_CLOSE_MS,
    connectionEventProperties,
    connectErrorToastKey,
    disconnectToastKey,
    resultToastKey,
    trackConnectionEvent,
    toastKey,
} from '../constants';

const useConnections = translate => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [connections, setConnections] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const adminMode = useSelector(getAdminModeEnabled);

    const notify = useCallback(
        (notifier, key, params) => notifier(translate(key, params), { autoClose: TOAST_AUTO_CLOSE_MS }),
        [translate]
    );

    const refresh = useCallback(async () => {
        try {
            setConnections(await fetchConnections(adminMode));
            setLoadError(null);
        } catch (error) {
            setLoadError(error);
        }
    }, [adminMode]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const clearBusyOnRestore = event => {
            if (event.persisted) setBusyId(null);
        };
        window.addEventListener('pageshow', clearBusyOnRestore);
        return () => window.removeEventListener('pageshow', clearBusyOnRestore);
    }, []);

    const clearConnectionParams = useCallback(() => {
        setSearchParams(
            previous => {
                const next = new URLSearchParams(previous);
                CONNECTION_URL_PARAMS.forEach(param => next.delete(param));
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    const consumedParamsRef = useRef(null);

    useEffect(() => {
        const result = searchParams.get(RESULT_PARAM);
        const errorCode = searchParams.get(ERROR_PARAM);
        const connectionId = searchParams.get(CONNECTION_ID_PARAM);
        if (!result && !errorCode) return;

        const consumed = `${connectionId}|${result}|${errorCode}`;
        if (consumedParamsRef.current === consumed) return;
        consumedParamsRef.current = consumed;
        clearConnectionParams();

        if (result) {
            notify(toaster.success, resultToastKey(result));
            trackConnectionEvent(CONNECTION_EVENTS.CONNECTED, { connectionId, result });
            refresh();
        } else {
            notify(toaster.error, connectErrorToastKey(errorCode));
            trackConnectionEvent(CONNECTION_EVENTS.CONNECT_FAILED, {
                connectionId,
                errorCode,
                stage: 'callback',
            });
        }
    }, [searchParams, clearConnectionParams, notify, refresh]);

    const connect = useCallback(
        async (connectionId, surface) => {
            const connection = connections.find(item => item.id === connectionId);
            const eventProperties = connectionEventProperties(connection, surface);
            setBusyId(connectionId);
            try {
                const refreshResult = await ensureINSToken();
                if (!refreshResult.ok) {
                    handleFailedRefresh(refreshResult);
                    throw new Error('interactive service session could not be refreshed');
                }
                const { authorize_url: authorizeUrl } = await startConnection(connectionId, adminMode);
                trackConnectionEvent(CONNECTION_EVENTS.OAUTH_STARTED, eventProperties);
                window.location.href = authorizeUrl;
            } catch {
                setBusyId(null);
                notify(toaster.error, toastKey(`ERRORS.${GENERIC_ERROR_KEY}`));
                trackConnectionEvent(CONNECTION_EVENTS.CONNECT_FAILED, {
                    ...eventProperties,
                    stage: 'start',
                });
            }
        },
        [adminMode, connections, notify]
    );

    const disconnect = useCallback(
        async (connectionId, surface) => {
            const connection = connections.find(item => item.id === connectionId);
            const eventProperties = connectionEventProperties(connection, surface);
            trackConnectionEvent(CONNECTION_EVENTS.DISCONNECT_CLICKED, eventProperties);
            setBusyId(connectionId);
            try {
                await disconnectConnection(connectionId);
                notify(toaster.success, disconnectToastKey(connection?.type), { name: connection?.display_name });
                trackConnectionEvent(CONNECTION_EVENTS.DISCONNECTED, eventProperties);
                await refresh();
            } catch {
                notify(toaster.error, toastKey(`ERRORS.${GENERIC_ERROR_KEY}`));
                trackConnectionEvent(CONNECTION_EVENTS.DISCONNECT_FAILED, eventProperties);
            } finally {
                setBusyId(null);
            }
        },
        [connections, notify, refresh]
    );

    return { connections, loadError, busyId, refresh, connect, disconnect };
};

export default useConnections;
